import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { ConfirmPhraseText, DELETE_FOREVER_PHRASE } from 'components/deletion/DeleteForeverDialog';

export type OrganizationDeletionPreviewData = {
  organizationID?: string;
  organizationName?: string;
  portalCount?: number;
  portals?: Array<{
    portalID?: string;
    portalName?: string;
    stackName?: string;
    pgvectorRowCount?: string;
  }>;
  dynamoCounts?: Record<string, string> | string;
  pgvectorRowCount?: string;
  repositoryBucketCount?: number;
  stacksToDestroy?: string[];
};

const parseDynamoCounts = (
  dynamoCounts: OrganizationDeletionPreviewData['dynamoCounts'],
): Record<string, string> => {
  if (!dynamoCounts) return {};
  if (typeof dynamoCounts === 'string') {
    try {
      return JSON.parse(dynamoCounts) as Record<string, string>;
    } catch {
      return {};
    }
  }
  return dynamoCounts;
};

const PreviewBody = ({ data }: { data: OrganizationDeletionPreviewData }) => {
  const dynamoCounts = parseDynamoCounts(data.dynamoCounts);
  const dynamoEntries = Object.entries(dynamoCounts)
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2">
        Organization
        {' '}
        <strong>{data.organizationName || data.organizationID}</strong>
        {' '}
        with
        {' '}
        {data.portalCount ?? 0}
        {' '}
        portal(s) will be hidden from the org picker and permanently purged after 30 days.
      </Typography>

      {data.stacksToDestroy && data.stacksToDestroy.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>CloudFormation stacks</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {data.stacksToDestroy.map((stack) => (
              <Typography component="li" variant="body2" key={stack}>{stack}</Typography>
            ))}
          </Box>
        </Box>
      )}

      {dynamoEntries.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>DynamoDB records (approx.)</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {dynamoEntries.map(([table, count]) => (
              <Typography component="li" variant="body2" key={table}>
                {table}
                :
                {' '}
                {count ?? '0'}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {data.pgvectorRowCount != null && data.pgvectorRowCount !== '' && (
        <Typography variant="body2">
          pgvector:
          {' '}
          {data.pgvectorRowCount}
        </Typography>
      )}

      {typeof data.repositoryBucketCount === 'number' && (
        <Typography variant="body2">
          Repository buckets:
          {' '}
          {data.repositoryBucketCount}
        </Typography>
      )}

      {data.portals && data.portals.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>Portals</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {data.portals.map((portal) => (
              <Typography component="li" variant="body2" key={portal.portalID}>
                {portal.portalName || portal.portalID}
                {portal.pgvectorRowCount ? ` (${portal.pgvectorRowCount})` : ''}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

type OrganizationDeleteDialogProps = {
  open: boolean;
  orgName?: string;
  loading: boolean;
  loadError?: string;
  previewData: OrganizationDeletionPreviewData | null;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
};

const OrganizationDeleteDialog = ({
  open,
  orgName,
  loading,
  loadError,
  previewData,
  onClose,
  onConfirm,
}: OrganizationDeleteDialogProps) => {
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const matched = phrase === DELETE_FOREVER_PHRASE;

  useEffect(() => {
    if (!open) setPhrase('');
  }, [open]);

  const handleClose = () => {
    if (busy) return;
    setPhrase('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!matched || busy || loading || !previewData) return;
    setBusy(true);
    try {
      await onConfirm();
      setPhrase('');
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Delete organization
        {orgName ? `: ${orgName}` : ''}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2">Loading deletion preview…</Typography>
          </Box>
        )}
        {!loading && loadError && (
          <Typography color="error" variant="body2">{loadError}</Typography>
        )}
        {!loading && !loadError && previewData && (
          <>
            <PreviewBody data={previewData} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
              Type
              {' '}
              <ConfirmPhraseText />
              {' '}
              to confirm:
            </Typography>
            <TextField
              fullWidth
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              disabled={busy}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          disabled={loading || !!loadError || !previewData || !matched || busy}
          onClick={() => { handleConfirm().catch(() => {}); }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationDeleteDialog;
